import { Option, Result, Schema, SchemaIssue } from "effect";
import { schemaParser, schemaDecoder } from "./schema-compat.ts";

const PositiveSafeInteger = /* @__PURE__ */ (() => Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)))();
export const StationPathLimits = /* @__PURE__ */ (() => Schema.Struct({
  maxBytes: PositiveSafeInteger,
  maxSegmentBytes: PositiveSafeInteger,
  maxSegments: PositiveSafeInteger
}))();
export type StationPathLimits = typeof StationPathLimits.Type;
export const DEFAULT_STATION_PATH_LIMITS: StationPathLimits = Object.freeze({
  maxBytes: 512, maxSegmentBytes: 255, maxSegments: 64
});

const PATH_MESSAGES = {
  "not-string": "Station path must be a string.",
  empty: "Station path must not be empty.",
  absolute: "Station path must be relative.",
  backslash: "Station path must use forward slashes.",
  "control-character": "Station path must not contain control characters.",
  "non-canonical-unicode": "Station path must use NFC-normalized Unicode.",
  "empty-segment": "Station path must not contain empty segments.",
  "dot-segment": "Station path must not contain '.' or '..' segments.",
  "path-bytes": "Station path exceeds its byte limit.",
  "segment-bytes": "Station path segment exceeds its byte limit.",
  "segment-count": "Station path is too deeply nested."
} as const;
export const StationPathIssue = /* @__PURE__ */ (() => Schema.Literals([
  "not-string", "empty", "absolute", "backslash", "control-character",
  "non-canonical-unicode", "empty-segment", "dot-segment", "path-bytes",
  "segment-bytes", "segment-count"
]))();
export type StationPathIssue = typeof StationPathIssue.Type;

export class StationPathLimitsError extends Schema.TaggedError<StationPathLimitsError>()(
  "StationPathLimitsError", { message: Schema.String, status: Schema.tag(400) }
) {}
const formatIssue = SchemaIssue.makeFormatterStandardSchemaV1();
const limitsContract = /* @__PURE__ */ (() => schemaDecoder(StationPathLimits, error => {
  const issue = formatIssue(error.issue).issues[0];
  return new StationPathLimitsError({ message: `${String(issue?.path?.[0] ?? "limits")} must be a positive safe integer.` });
}))();
const bytes = (value: string) => new TextEncoder().encode(value).byteLength;
const pathCheck = (issue: StationPathIssue, predicate: (value: string) => boolean) =>
  Schema.makeFilter(predicate, { identifier: issue, message: PATH_MESSAGES[issue] }, true);

/** Limits participate in the Schema itself, including byte and Unicode rules. */
export function stationPathSchema(input: StationPathLimits = DEFAULT_STATION_PATH_LIMITS) {
  const limits = limitsContract.parse(input);
  const Segment = Schema.String.check(
    pathCheck("empty-segment", value => value.length > 0),
    pathCheck("dot-segment", value => value !== "." && value !== ".."),
    pathCheck("segment-bytes", value => bytes(value) <= limits.maxSegmentBytes)
  );
  const segments = Schema.decodeUnknownResult(Schema.Array(Segment));
  return Schema.String.check(
    pathCheck("empty", value => value.length > 0),
    pathCheck("absolute", value => !value.startsWith("/")),
    pathCheck("backslash", value => !value.includes("\\")),
    pathCheck("control-character", value => !/[\x00-\x1f\x7f]/.test(value)),
    pathCheck("non-canonical-unicode", value => value.normalize("NFC") === value),
    pathCheck("path-bytes", value => bytes(value) <= limits.maxBytes),
    pathCheck("segment-count", value => value.split("/").length <= limits.maxSegments),
    Schema.makeFilter(value => Result.match(segments(value.split("/")), {
      onSuccess: () => true,
      onFailure: error => error.issue
    }))
  ).pipe(Schema.brand("station-path"));
}

/** A normalized, relative source path within one station workspace. */
export const StationPath = stationPathSchema();
export type StationPath = typeof StationPath.Type;
const PathFailure = /* @__PURE__ */ (() => Schema.Struct({
  ok: Schema.Literal(false), issue: StationPathIssue, message: Schema.String,
  actual: Schema.Number.pipe(Schema.optional), limit: Schema.Number.pipe(Schema.optional)
}))();
export const StationPathValidation = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ ok: Schema.Literal(true), path: StationPath }), PathFailure
]))();
export type StationPathValidation = typeof StationPathValidation.Type;

// Project Schema's first issue into the historical diagnostic fields. No path
// predicate is repeated here; its code comes from the check that failed.
function pathIssue(issue: SchemaIssue.Issue, segmentIndex?: number): { code: StationPathIssue; segmentIndex?: number } {
  if (issue._tag === "Filter") {
    const code = Schema.decodeUnknownOption(StationPathIssue)(issue.filter.annotations?.identifier);
    return Option.isSome(code) ? { code: code.value, segmentIndex } : pathIssue(issue.issue, segmentIndex);
  }
  if (issue._tag === "Pointer") {
    return pathIssue(issue.issue, typeof issue.path[0] === "number" ? issue.path[0] : segmentIndex);
  }
  if (issue._tag === "Composite") return pathIssue(issue.issues[0], segmentIndex);
  return { code: "not-string" };
}

function failureResult(error: Schema.SchemaError, value: unknown, limits: StationPathLimits): typeof PathFailure.Type {
  const { code, segmentIndex } = pathIssue(error.issue);
  const bounds = Schema.is(Schema.String)(value)
    ? code === "path-bytes" ? { actual: bytes(value), limit: limits.maxBytes }
      : code === "segment-count" ? { actual: value.split("/").length, limit: limits.maxSegments }
      : code === "segment-bytes" ? { actual: bytes(value.split("/")[segmentIndex ?? 0]), limit: limits.maxSegmentBytes }
      : {} : {};
  return { ok: false, issue: code, message: PATH_MESSAGES[code], ...bounds };
}

const schemaWithLimits = (limits: StationPathLimits) =>
  limits === DEFAULT_STATION_PATH_LIMITS ? StationPath : stationPathSchema(limits);
export const validateStationPath = (
  value: unknown, limits: StationPathLimits = DEFAULT_STATION_PATH_LIMITS
): StationPathValidation => Result.match(Schema.decodeUnknownResult(schemaWithLimits(limits))(value), {
  onSuccess: path => ({ ok: true as const, path }),
  onFailure: error => failureResult(error, value, limits)
});

const PathErrorFields = {
  issue: StationPathIssue, message: Schema.String, status: Schema.tag(400),
  actual: Schema.Number.pipe(Schema.optional), limit: Schema.Number.pipe(Schema.optional)
};
export class StationPathValidationError extends Schema.TaggedError<StationPathValidationError>()(
  "StationPathValidationError", PathErrorFields
) {
  constructor(result: typeof PathFailure.Type | Schema.Struct<typeof PathErrorFields>["~type.make.in"], options?: Schema.MakeOptions) {
    super(result, options);
  }
}

export const stationPath = (value: unknown, limits: StationPathLimits = DEFAULT_STATION_PATH_LIMITS): StationPath =>
  schemaParser(schemaWithLimits(limits), error => new StationPathValidationError(failureResult(error, value, limits)))(value);
export const isStationPath = /* @__PURE__ */ (() => Schema.is(StationPath))();
